import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Loader2, Circle, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

const STATUS_INFO = {
  active: { label: "Working", dot: "bg-emerald-500 animate-pulse", variant: "default" },
  on_break: { label: "On break", dot: "bg-amber-500", variant: "secondary" },
  completed: { label: "Shift done", dot: "bg-zinc-400", variant: "outline" },
  offline: { label: "Offline", dot: "bg-zinc-300", variant: "outline" },
};

function fmtMinutes(m) {
  if (!m) return "0m";
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}

export default function AdminLiveStatus() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/shifts/live");
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const counts = rows.reduce(
    (acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    },
    { active: 0, on_break: 0, completed: 0, offline: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="admin-live-status">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: "active", label: "Working" },
          { key: "on_break", label: "On break" },
          { key: "completed", label: "Done" },
          { key: "offline", label: "Offline" },
        ].map((s) => (
          <Card
            key={s.key}
            className="overflow-hidden"
            data-testid={`live-count-${s.key}`}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
                <div className="font-display text-3xl font-bold tabular-nums">
                  {counts[s.key]}
                </div>
              </div>
              <Circle
                className={`h-3 w-3 rounded-full ${STATUS_INFO[s.key].dot}`}
                fill="currentColor"
              />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold">Team status</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          data-testid="live-refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="py-14 text-center text-muted-foreground">
              No employees yet. Add some on the Employees tab.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
              {rows.map((r) => {
                const info = STATUS_INFO[r.status];
                return (
                  <div
                    key={r.user_id}
                    data-testid={`live-row-${r.user_id}`}
                    className="group relative flex flex-col items-center text-center p-5 rounded-2xl border border-border bg-white hover:shadow-md hover:-translate-y-0.5 hover:border-[#6D28D9]/40 transition-all duration-200"
                  >
                    {/* Status dot overlay on avatar */}
                    <div className="relative">
                      <div className="h-24 w-24 sm:h-28 sm:w-28 rounded-2xl overflow-hidden ring-1 ring-black/5 bg-muted">
                        {r.photo ? (
                          <img
                            src={r.photo}
                            alt={r.name || r.email}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full dp-gradient-bg flex items-center justify-center text-white text-3xl font-display font-bold">
                            {(r.name || r.email).charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <span
                        className={`absolute -bottom-1 -right-1 h-6 w-6 rounded-full ring-4 ring-white ${info.dot}`}
                        title={info.label}
                      />
                    </div>

                    {/* Name */}
                    <div className="mt-4 font-semibold text-sm sm:text-base line-clamp-1 w-full">
                      {r.name || r.email}
                    </div>
                    <div className="text-xs text-muted-foreground line-clamp-1 w-full">
                      {r.email}
                    </div>

                    {/* Status badge */}
                    <Badge
                      variant={info.variant}
                      className="mt-3 font-medium"
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full mr-1.5 inline-block ${info.dot}`}
                      />
                      {info.label}
                    </Badge>

                    {/* Time info */}
                    <div className="mt-3 pt-3 border-t border-border w-full text-xs text-muted-foreground space-y-0.5">
                      {r.shift?.net_minutes !== undefined && r.shift !== null ? (
                        <>
                          <div className="font-semibold text-foreground tabular-nums">
                            {fmtMinutes(r.shift?.net_minutes || 0)}
                          </div>
                          {r.shift?.punch_in_at && (
                            <div>
                              In · {new Date(r.shift.punch_in_at).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}
                              {r.shift?.punch_out_at && (
                                <> · Out · {new Date(r.shift.punch_out_at).toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})}</>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div>Not punched in today</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
